/**
 * @fileoverview Molecule: one purchase and its amortisation state.
 * @module components/molecules/PurchaseCard
 */

import { Button, Icon, ProgressBar } from "@/components/atoms"
import {
	computeAmortisation,
	formatCents,
	formatRatio,
} from "@/lib/domain/amortisation"
import type { Purchase, PurchaseId } from "@/types/types"

import "./PurchaseCard.css"

/** Props accepted by {@link PurchaseCard}. */
export interface PurchaseCardProps {
	/** The purchase to display. */
	readonly purchase: Purchase
	/** Opens the full detail, edit and delete modal. */
	readonly onOpen: (id: PurchaseId) => void
	/** Records uses; a negative delta removes some. */
	readonly onRecordUse: (id: PurchaseId, delta: number) => void
}

/**
 * Compact purchase card, sized for a horizontal-scrolling row.
 *
 * @description
 * The head, category, progress and figures sit inside a single button that
 * opens {@link PurchaseDetailsModal}; the use-recording controls stay
 * outside it so the card never nests a button inside a button. The "−1"
 * control disappears rather than showing disabled once there is nothing
 * left to retract — a card with zero uses has no undo to offer.
 *
 * @param props - The purchase and the actions available on it.
 * @returns The rendered card.
 */
export function PurchaseCard({
	purchase,
	onOpen,
	onRecordUse,
}: PurchaseCardProps) {
	const amortisation = computeAmortisation(purchase)

	return (
		<li className="purchase">
			<button
				type="button"
				className="purchase__open"
				onClick={() => {
					onOpen(purchase.id)
				}}
				aria-label={`Voir le détail de ${purchase.label}`}
			>
				<div className="purchase__head">
					<div className="purchase__title-group">
						<h3 className="purchase__label">{purchase.label}</h3>
						{purchase.category !== undefined && (
							<span className="purchase__category">
								{purchase.category}
							</span>
						)}
					</div>
					<span className="purchase__price">
						{formatCents(purchase.priceCents)}
					</span>
				</div>

				<ProgressBar
					ratio={amortisation.ratio}
					label={`Amortissement de ${purchase.label}`}
				/>

				<div className="purchase__figures">
					<span className="purchase__figure">
						<strong>
							{purchase.currentUses} / {purchase.targetUses}
						</strong>{" "}
						utilisations
					</span>
					<span className="purchase__figure">
						<strong>
							{formatCents(amortisation.costPerUseCents)}
						</strong>{" "}
						/ usage
					</span>
					<span className="purchase__figure">
						{amortisation.isAmortised ? (
							<span className="purchase__done">🟢 Amorti</span>
						) : (
							<strong>{formatRatio(amortisation.ratio)}</strong>
						)}
					</span>
				</div>
			</button>

			<div className="purchase__actions">
				<Button
					size="sm"
					onClick={() => {
						onRecordUse(purchase.id, 1)
					}}
				>
					+1 utilisation
				</Button>
				{purchase.currentUses > 0 && (
					<Button
						size="sm"
						variant="ghost"
						iconOnly
						onClick={() => {
							onRecordUse(purchase.id, -1)
						}}
						ariaLabel={`Retirer une utilisation de ${purchase.label}`}
					>
						<Icon name="minus" size={16} />
					</Button>
				)}
			</div>
		</li>
	)
}
